import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES, VwAuthService } from 'vwappservices';
import { AppStateService } from '../../services/app-state.service';
import { DashboardApiService } from '../../services/dashboard-api.service';
import { LiveUpdatesService } from '../../services/live-updates.service';
import { UsersApiService } from '../../services/users-api.service';
import { notifyResult, reportApiError } from '../../shared/api-result.helper';
import { AppendToBodyDirective } from '../../shared/append-to-body.directive';
import { ConfirmService } from '../../shared/confirm.service';
import { CamelToTitlePipe } from '../../shared/camel-to-title.pipe';

@Component({
  selector: 'app-default-header',
  standalone: true,
  imports: [CommonModule, FormsModule, CamelToTitlePipe, AppendToBodyDirective],
  templateUrl: './default-header.component.html',
  styleUrl: './default-header.component.scss',
})
export class DefaultHeaderComponent implements OnInit, OnDestroy {
  isClassAdded: boolean = false;
  @Output() toggleSidebar = new EventEmitter<void>();

  passwordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
  showCurrentPassword = false;
  showNewPassword = false;
  changingPassword = false;

  // Downtime notifications — unfiltered across every product (unlike the Dashboard's own Alerts
  // card, which follows whatever single product its filter has selected), so a downtime on any
  // product is visible from any page, not just while looking at that specific product's filter.
  // Kept live via two socket paths: the dedicated 'downtime:update' topic (instant, pushed right
  // after a report/resume) and the broader 'alert:new' broadcast (periodic 5-minute tick — see
  // backend/services/alertEngine.service.js — a correctness net that self-heals any dropped or
  // out-of-order 'downtime:update' message), on top of the initial fetch on load and an on-demand
  // refresh whenever the bell itself is clicked.
  downtimeAlerts: any[] = [];

  private unsubscribeLiveUpdates?: () => void;

  @ViewChild('changePasswordCloseBtn') changePasswordCloseBtn!: ElementRef<HTMLButtonElement>;

  private toastr = inject(ToastrService);
  private usersApi = inject(UsersApiService);
  private confirmService = inject(ConfirmService);
  private dashboardApi = inject(DashboardApiService);
  private liveUpdates = inject(LiveUpdatesService);

  constructor(
    public appState: AppStateService,
    private auth: VwAuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.appState.isAdminOrSupervisor()) {
      this.loadAlerts();
      this.unsubscribeLiveUpdates = this.liveUpdates.connect({
        'alert:new': (alerts: any[]) => {
          this.downtimeAlerts = (alerts || []).filter((a) => a.type === 'downtime');
        },
        'downtime:update': ({ action, downtime }: { action: string; downtime: any }) => {
          if (action === 'reported') {
            if (!this.downtimeAlerts.some((a) => a._id === downtime._id)) {
              this.downtimeAlerts = [{ ...downtime, type: 'downtime' }, ...this.downtimeAlerts];
            }
            this.toastr.warning(
              `${downtime.operatorName} reported downtime: ${downtime.productName} — ${downtime.stepName}`,
              'Downtime Reported',
            );
          } else if (action === 'resolved') {
            this.downtimeAlerts = this.downtimeAlerts.filter((a) => a._id !== downtime._id);
          }
        },
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeLiveUpdates?.();
  }

  async loadAlerts() {
    const res = await this.dashboardApi.getAlerts();
    if (res.errorCode === VW_ERROR_CODES.SUCCESS) {
      this.downtimeAlerts = (res.Data.alerts || []).filter((a: any) => a.type === 'downtime');
    } else {
      reportApiError(this.toastr, res);
    }
  }

  formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  async logout() {
    const ok = await this.confirmService.confirm({
      title: 'Log out?',
      message: "You'll need to sign in again to continue.",
      confirmText: 'Logout',
      danger: true,
      icon: 'fa-sign-out-alt',
    });
    if (!ok) return;
    await this.auth.logout('/auth/logout');
    this.appState.clearProfile();
    this.router.navigateByUrl('/login');
  }

  onToggleSidebar() {
    this.toggleSidebar.emit();
  }

  toggleClsss() {
    this.isClassAdded = !this.isClassAdded;
  }

  openChangePasswordModal() {
    this.passwordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
    this.showCurrentPassword = false;
    this.showNewPassword = false;
  }

  async changePassword() {
    const { currentPassword, newPassword, confirmPassword } = this.passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) return;
    if (newPassword !== confirmPassword) {
      this.toastr.error('New password and confirm password do not match.');
      return;
    }
    this.changingPassword = true;
    const res = await this.usersApi.changeOwnPassword(currentPassword, newPassword);
    this.changingPassword = false;
    if (!notifyResult(this.toastr, res, 'Password changed successfully.')) return;
    this.changePasswordCloseBtn.nativeElement.click();
  }
}
